import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  ChevronRight, Plus, Smile, Meh, Frown,
  Pencil, Trash2, Mail, Link as LinkIcon, RefreshCw,
} from 'lucide-react';
import { Avatar } from '../components/Avatar';
import { KineHeader } from '../components/kine/KineHeader';
import { KineSidebar } from '../components/kine/KineSidebar';
import { formatRelativeDate } from '../../lib/utils';
import {
  useDetallePaciente,
  type DetallePacienteData,
  type SesionReciente,
} from '@/lib/supabase/queries/useDetallePaciente';
import {
  usePlanesActivos,
  type PlanActivo,
} from '@/lib/supabase/queries/usePlanesActivos';
import { useEditarPaciente } from '@/lib/supabase/queries/useEditarPaciente';
import { useEliminarPaciente } from '@/lib/supabase/queries/useEliminarPaciente';
import { useRegenerarInvitacion } from '@/lib/supabase/queries/useRegenerarInvitacion';
import {
  PatientFormDialog,
  type PatientFormCreateData,
  type PatientFormEditData,
} from '../components/patients/PatientFormDialog';
import { InvitacionModal } from '../components/patients/InvitacionModal';
import { ChatConversation } from '../components/patients/ChatConversation';
import { useMensajesConversacion } from '@/lib/supabase/queries/useMensajesConversacion';
import { useEnviarMensaje } from '@/lib/supabase/queries/useEnviarMensaje';
import { useMarcarLeidos } from '@/lib/supabase/queries/useMarcarLeidos';
import {
  useActualizarPlanEjercicio,
  type EditableField,
} from '@/lib/supabase/queries/useActualizarPlanEjercicio';
import {
  useEliminarPlanEjercicio,
  type DeleteError,
} from '@/lib/supabase/queries/useEliminarPlanEjercicio';
import { useSesionesPaciente } from '@/lib/supabase/queries/useSesionesPaciente';
import {
  SessionListGrouped,
  type SessionListItem,
} from '@/app/components/sessions/SessionListGrouped';
import { isVideoOnlyExercise } from '@/lib/pose/exerciseSchemas';
import { PlayCircle } from 'lucide-react';
import { useEvolucionPaciente } from '@/lib/paciente/useEvolucionPaciente';
import { EvolucionChart } from '../components/charts/EvolucionChart';
import {
  useAdherenciaPaciente,
  type AdherenciaResult,
} from '@/lib/supabase/queries/useAdherenciaPaciente';

type TabKey = 'resumen' | 'sesiones' | 'rutinas' | 'mensajes' | 'notas';

const VALID_TABS: readonly TabKey[] = [
  'resumen', 'sesiones', 'rutinas', 'mensajes', 'notas',
] as const;

function isValidTab(s: string | null): s is TabKey {
  return s !== null && (VALID_TABS as readonly string[]).includes(s);
}

interface InvitacionState {
  signupToken: string;
  pacienteNombre: string;
  pacienteTelefono: string | null;
  expiresAt: string;
  variant: 'actualizada' | 'actual';
}

export function DetallePaciente() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const { data, errorKind, errorMessage, loading, refetch } = useDetallePaciente(patientId);
  // Sprint 3 Approach A: el "inicio del plan activo" es la referencia
  // temporal para el subtitle del paciente. Con contenedor invisible, la
  // entidad tratamiento no se expone — semanaLabel deriva del plan activo
  // más reciente (usePlanesActivos ordena por fecha_inicio DESC).
  const { data: planesActivos } = usePlanesActivos(patientId);
  const primerPlanFechaInicio = planesActivos?.[0]?.fecha_inicio ?? null;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [invitacion, setInvitacion] = useState<InvitacionState | null>(null);

  const editarHook = useEditarPaciente();
  const eliminarHook = useEliminarPaciente();
  const regenerarHook = useRegenerarInvitacion();

  const handleEditar = async (
    formData: PatientFormCreateData | PatientFormEditData,
  ) => {
    if (!patientId) return;
    // En modo "editar" siempre es PatientFormEditData (con fecha_nacimiento).
    const editData = formData as PatientFormEditData;
    const ok = await editarHook.editar(patientId, editData);
    if (!ok) return;
    setEditOpen(false);
    editarHook.reset();
    void refetch();
  };

  const handleEliminar = async () => {
    if (!patientId) return;
    const ok = await eliminarHook.eliminar(patientId);
    if (!ok) return;
    // Success — navega al dashboard.
    navigate('/');
  };

  const handleCopiarLinkActual = () => {
    if (!data?.paciente.signupToken || !data?.paciente.signupTokenExpiresAt) return;
    setInvitacion({
      signupToken: data.paciente.signupToken,
      pacienteNombre: data.paciente.nombre,
      pacienteTelefono: data.paciente.telefono,
      expiresAt: data.paciente.signupTokenExpiresAt,
      variant: 'actual',
    });
  };

  const handleRegenerar = async () => {
    if (!patientId || !data) return;
    const result = await regenerarHook.regenerar(patientId);
    if (!result) return;
    setInvitacion({
      signupToken: result.signupToken,
      pacienteNombre: data.paciente.nombre,
      pacienteTelefono: data.paciente.telefono,
      expiresAt: result.expiresAt,
      variant: 'actualizada',
    });
    void refetch();
  };

  return (
    <div className="min-h-screen bg-background transition-colors duration-200">
      <KineHeader
        kineNombre={data?.kineNombre}
        onMenuClick={() => setDrawerOpen(true)}
      />

      <div className="flex">
        <KineSidebar
          activeKey="pacientes"
          mobileOpen={drawerOpen}
          onMobileClose={() => setDrawerOpen(false)}
        />

        <div className="flex-1 p-4 md:p-8 min-w-0">
          <div className="max-w-[1400px]">
            {loading && <DetalleSkeleton />}
            {errorKind === 'not-found' && (
              <NotFoundState onVolver={() => navigate('/')} />
            )}
            {errorKind === 'fetch-error' && (
              <FetchErrorState
                message={errorMessage ?? ''}
                onVolver={() => navigate('/')}
              />
            )}
            {data && !loading && !errorKind && (
              <DetalleContent
                patientId={patientId}
                data={data}
                primerPlanFechaInicio={primerPlanFechaInicio}
                onPrescribirRutina={() =>
                  navigate(`/prescribir/${patientId}`)
                }
                onEditar={() => {
                  editarHook.reset();
                  setEditOpen(true);
                }}
                onEliminar={() => {
                  eliminarHook.reset();
                  setDeleteConfirmOpen(true);
                }}
                onCopiarLinkActual={handleCopiarLinkActual}
                onRegenerar={handleRegenerar}
                regenerando={regenerarHook.loading}
              />
            )}
          </div>
        </div>
      </div>

      {data && (
        <PatientFormDialog
          open={editOpen}
          onOpenChange={(o) => {
            if (!editarHook.loading) setEditOpen(o);
          }}
          mode="editar"
          initial={{
            nombre: data.paciente.nombre,
            apellido: data.paciente.apellido,
            email: data.paciente.email ?? '',
            telefono: data.paciente.telefono,
            fecha_nacimiento: data.paciente.fecha_nacimiento,
            notas_clinicas: data.paciente.notas_clinicas,
          }}
          submitting={editarHook.loading}
          errorMessage={editarHook.error}
          onSave={handleEditar}
        />
      )}

      {data && (
        <DeleteConfirmDialog
          open={deleteConfirmOpen}
          onOpenChange={setDeleteConfirmOpen}
          pacienteNombre={`${data.paciente.nombre} ${data.paciente.apellido}`}
          onConfirm={handleEliminar}
          submitting={eliminarHook.loading}
          errorMessage={eliminarHook.error?.message ?? null}
        />
      )}

      {invitacion && (
        <InvitacionModal
          open={invitacion !== null}
          onOpenChange={(o) => {
            if (!o) setInvitacion(null);
          }}
          signupToken={invitacion.signupToken}
          pacienteNombre={invitacion.pacienteNombre}
          pacienteTelefono={invitacion.pacienteTelefono}
          expiresAt={invitacion.expiresAt}
          variant={invitacion.variant}
        />
      )}

      {regenerarHook.error && !invitacion && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-red-50 border border-red-200 rounded-lg px-4 py-2 shadow-lg max-w-md">
          <p className="text-sm text-red-700">{regenerarHook.error}</p>
        </div>
      )}
    </div>
  );
}

// ─── Content (data OK) ────────────────────────────────────────────────

function DetalleContent({
  patientId,
  data,
  primerPlanFechaInicio,
  onPrescribirRutina,
  onEditar,
  onEliminar,
  onCopiarLinkActual,
  onRegenerar,
  regenerando,
}: {
  patientId: string | undefined;
  data: DetallePacienteData;
  /** ISO date del plan activo más reciente. null si no hay plan activo. */
  primerPlanFechaInicio: string | null;
  onPrescribirRutina: () => void;
  onEditar: () => void;
  onEliminar: () => void;
  onCopiarLinkActual: () => void;
  onRegenerar: () => void;
  regenerando: boolean;
}) {
  // Tab activo — sincronizado con URL param `?tab=`. Entrada desde
  // /mensajes-kine + kebab "Enviar mensaje" abre directo en tab Mensajes.
  // Sin param → 'resumen' default (UX previa preservada).
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialTab: TabKey = isValidTab(tabFromUrl) ? tabFromUrl : 'resumen';
  const [activeTab, setActiveTabState] = useState<TabKey>(initialTab);

  const setActiveTab = (tab: TabKey) => {
    setActiveTabState(tab);
    if (tab === 'resumen') {
      // Limpiamos el param si vuelven a default — URL queda limpia.
      const next = new URLSearchParams(searchParams);
      next.delete('tab');
      setSearchParams(next, { replace: true });
    } else {
      const next = new URLSearchParams(searchParams);
      next.set('tab', tab);
      setSearchParams(next, { replace: true });
    }
  };

  // Sync de vuelta cuando el URL cambia externamente (browser back/forward).
  useEffect(() => {
    if (isValidTab(tabFromUrl) && tabFromUrl !== activeTab) {
      setActiveTabState(tabFromUrl);
    } else if (tabFromUrl === null && activeTab !== 'resumen') {
      setActiveTabState('resumen');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFromUrl]);
  const evolucion = useEvolucionPaciente(patientId ?? null);
  const rom = evolucion.data?.rom ?? null;
  const dolorSemanas = evolucion.data?.dolorSemanas ?? [];
  const fullName = `${data.paciente.nombre} ${data.paciente.apellido}`;
  const edadLabel = formatEdad(data.paciente.fecha_nacimiento);
  const semanaLabel = primerPlanFechaInicio
    ? formatSemana(primerPlanFechaInicio, null)
    : null;
  // Subtitle: "edad · semana del plan activo", omitiendo segmentos vacíos.
  const subtitle = [edadLabel, semanaLabel]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
        <Link
          to="/"
          className="hover:text-primary transition-colors duration-200"
        >
          Pacientes
        </Link>
        <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
        <span>{fullName}</span>
      </div>

      {/* Identity card XL */}
      <div className="bg-card rounded-2xl p-4 md:p-8 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)] mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <Avatar name={fullName} size="xl" colorIndex={0} />
            <div className="min-w-0">
              <h1 className="text-2xl md:text-3xl mb-1 truncate">{fullName}</h1>
              <p className="text-sm md:text-base text-muted-foreground">
                {subtitle || 'Sin información disponible'}
              </p>
            </div>
          </div>
          <div className="flex flex-row md:gap-8 gap-3 justify-around md:justify-end">
            <AdherenciaInlineStat pacienteId={patientId} />
            <InlineStat
              value={data.totalSesiones}
              label="Sesiones"
              hint="Completadas"
            />
            <InlineStat
              value={
                rom ? `${rom.ganado >= 0 ? '+' : ''}${rom.ganado}°` : '—'
              }
              label="ROM ganado"
              hint={rom ? `en ${rom.semanas.length} semanas` : 'Sin mediciones'}
            />
          </div>
        </div>
      </div>

      {/* Action bar — acciones primarias del kine sobre el paciente. */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <button
          onClick={onPrescribirRutina}
          className="bg-primary text-primary-foreground px-5 py-2.5 rounded-full flex items-center gap-2 text-sm font-semibold shadow-[0_2px_4px_rgba(0,59,74,0.2)] transition-all duration-200 ease-out hover:shadow-[0_4px_8px_rgba(0,59,74,0.3)] hover:scale-[1.02]"
        >
          <Plus className="w-4 h-4" strokeWidth={1.5} />
          Prescribir rutina nueva
        </button>

        {/* Botones secundarios — Editar siempre disponible, los de invitación
            solo si el paciente no completó signup todavía. */}
        <button
          onClick={onEditar}
          className="bg-card border border-border px-4 py-2.5 rounded-full flex items-center gap-2 text-sm font-medium transition-all duration-200 ease-out hover:bg-muted/50"
        >
          <Pencil className="w-4 h-4" strokeWidth={1.5} />
          Editar
        </button>

        {data.paciente.invitationStatus === 'invited' && (
          <InvitationActions
            expiresAt={data.paciente.signupTokenExpiresAt}
            onCopiar={onCopiarLinkActual}
            onRegenerar={onRegenerar}
            regenerando={regenerando}
          />
        )}

        <button
          onClick={onEliminar}
          className="ml-auto text-red-600 border border-red-200 bg-red-50/50 px-4 py-2.5 rounded-full flex items-center gap-2 text-sm font-medium transition-all duration-200 ease-out hover:bg-red-100"
        >
          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
          Eliminar
        </button>
      </div>

      {data.paciente.invitationStatus === 'invited' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 flex items-start gap-3">
          <Mail className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" strokeWidth={1.5} />
          <div className="text-sm">
            <p className="font-medium text-amber-900">
              Invitación enviada — todavía no activó su cuenta.
            </p>
            <p className="text-amber-800/80 text-xs mt-0.5">
              Copiá el link o regeneralo si venció para que el paciente pueda activar su cuenta.
            </p>
          </div>
        </div>
      )}

      {/* Tabs strip — todos con contenido. Resumen es el default. */}
      <div className="flex gap-2 mb-6 border-b border-border overflow-x-auto">
        <TabButton
          label="Resumen"
          active={activeTab === 'resumen'}
          onClick={() => setActiveTab('resumen')}
        />
        <TabButton
          label="Sesiones"
          active={activeTab === 'sesiones'}
          onClick={() => setActiveTab('sesiones')}
        />
        <TabButton
          label="Rutinas"
          active={activeTab === 'rutinas'}
          onClick={() => setActiveTab('rutinas')}
        />
        <TabButton
          label="Mensajes"
          active={activeTab === 'mensajes'}
          onClick={() => setActiveTab('mensajes')}
        />
        <TabButton
          label="Notas"
          active={activeTab === 'notas'}
          onClick={() => setActiveTab('notas')}
        />
      </div>

      {activeTab === 'rutinas' && (
        <TabRutinasContent
          patientId={patientId}
          onPrescribirRutina={onPrescribirRutina}
        />
      )}

      {activeTab === 'mensajes' && patientId && (
        <TabMensajesContent
          patientId={patientId}
          pacienteNoActivo={data.paciente.invitationStatus === 'invited'}
        />
      )}

      {activeTab === 'sesiones' && (
        <TabSesionesContent patientId={patientId} />
      )}

      {activeTab === 'notas' && (
        <TabNotasContent
          notas={data.paciente.notas_clinicas}
          onEditar={onEditar}
        />
      )}

      {activeTab === 'resumen' && (
        <>
      {/* Adherencia 30d + curvas de ROM (pose_analysis) y dolor. */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6 mb-6">
        <Adherencia30dCard pacienteId={patientId} />
        <EvolucionChart
          titulo="Rango de movimiento"
          subtitulo={rom ? `${rom.ejercicioNombre} · grados` : 'Grados por semana'}
          data={rom?.semanas ?? []}
          unidad="°"
          color="var(--chart-2)"
          destacado={
            rom
              ? {
                  valor: `${rom.ganado >= 0 ? '+' : ''}${rom.ganado}°`,
                  label: 'ganados',
                  tono: rom.ganado > 0 ? 'bueno' : 'neutro',
                }
              : undefined
          }
        />
        <EvolucionChart
          titulo="Dolor reportado"
          subtitulo="Promedio semanal · escala 0-10"
          data={dolorSemanas}
          unidad="/10"
          color="var(--chart-4)"
          dominio={[0, 10]}
          destacado={
            dolorSemanas.length > 0
              ? {
                  valor: `${dolorSemanas[dolorSemanas.length - 1].value}/10`,
                  label: 'esta semana',
                }
              : undefined
          }
          vacio="Todavía no reportó dolor en sus sesiones"
        />
      </div>

      {/* Sesiones recientes */}
      <div className="bg-card rounded-2xl p-4 md:p-6 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)]">
        <h3 className="mb-6 font-semibold">Sesiones recientes</h3>
        {data.sesionesRecientes.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            Este paciente aún no completó sesiones.
          </p>
        ) : (
          <div className="space-y-4">
            {data.sesionesRecientes.map((s) => (
              <SesionRecienteRow key={s.id} sesion={s} />
            ))}
          </div>
        )}
      </div>
        </>
      )}
    </>
  );
}

function TabButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 md:px-6 py-3 whitespace-nowrap transition-all duration-200 ease-out ${
        active
          ? 'border-b-2 border-primary text-primary font-semibold'
          : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}
    </button>
  );
}

/** Tab Notas — notas clínicas del paciente (pacientes.notas_clinicas).
 *  Se editan desde el dialog "Editar" del paciente. */
function TabNotasContent({
  notas,
  onEditar,
}: {
  notas: string | null;
  onEditar: () => void;
}) {
  return (
    <div className="bg-card rounded-2xl p-4 md:p-6 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)]">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold">Notas clínicas</h3>
        <button
          onClick={onEditar}
          className="text-sm text-primary font-medium flex items-center gap-1.5 hover:underline"
        >
          <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
          Editar
        </button>
      </div>
      {notas ? (
        <p className="text-sm leading-relaxed whitespace-pre-line">{notas}</p>
      ) : (
        <p className="text-sm text-muted-foreground py-6 text-center">
          Todavía no cargaste notas clínicas para este paciente.
        </p>
      )}
    </div>
  );
}

/** Tab Sesiones — lista de sesiones del paciente agrupadas por fecha.
 *  Click en una completada abre la vista kine del detalle
 *  (/sesion-kine/:id). */
function TabSesionesContent({
  patientId,
}: {
  patientId: string | undefined;
}) {
  const { data: sesiones, error, loading } = useSesionesPaciente(patientId);
  const navigate = useNavigate();

  if (loading) {
    return (
      <p className="text-center text-muted-foreground py-12">
        Cargando sesiones…
      </p>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-2xl p-6 border border-border text-center">
        <p className="text-orange-600 font-semibold mb-2">
          No pudimos cargar las sesiones
        </p>
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (!sesiones || sesiones.length === 0) {
    return (
      <div className="bg-card rounded-2xl p-8 border border-border text-center">
        <h3 className="text-lg font-semibold mb-2">
          Este paciente todavía no completó sesiones
        </h3>
        <p className="text-sm text-muted-foreground">
          Cuando el paciente termine su primera sesión, aparecerá acá con
          reps, calidad y observaciones.
        </p>
      </div>
    );
  }

  return (
    <SessionListGrouped
      sesiones={sesiones as SessionListItem[]}
      showPatientName={false}
      onItemClick={(s) => {
        if (s.estado === 'completada') navigate(`/sesion-kine/${s.id}`);
      }}
    />
  );
}

function TabMensajesContent({
  patientId,
  pacienteNoActivo,
}: {
  patientId: string;
  pacienteNoActivo: boolean;
}) {
  const { data, error, loading } = useMensajesConversacion(patientId);
  const enviar = useEnviarMensaje();
  const marcar = useMarcarLeidos();

  // Si el paciente no activó su cuenta todavía, no puede recibir ni
  // responder mensajes — bloqueamos el envío y mostramos copy.
  if (pacienteNoActivo) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8 text-center">
        <p className="font-medium text-amber-900 mb-1">
          El paciente todavía no activó su cuenta
        </p>
        <p className="text-sm text-amber-800/80">
          Los mensajes están disponibles una vez que el paciente complete su
          signup vía el link de invitación.
        </p>
      </div>
    );
  }

  return (
    <ChatConversation
      mensajes={data ?? []}
      viewerRole="kine"
      loading={loading}
      fetchError={error}
      sending={enviar.loading}
      sendError={enviar.error}
      onSend={async (content) => {
        const result = await enviar.enviar({
          pacienteId: patientId,
          sender: 'kine',
          content,
        });
        return result !== null;
      }}
      onMarkRead={() => {
        void marcar.marcar(patientId);
      }}
    />
  );
}

function TabRutinasContent({
  patientId,
  onPrescribirRutina,
}: {
  patientId: string | undefined;
  onPrescribirRutina: () => void;
}) {
  const { data: planes, error, loading, refetch } = usePlanesActivos(patientId);
  // Toast efímero post-delete OK. null = sin toast. setTimeout en el callback
  // del modal dispara el dismiss después de 3s.
  const [toast, setToast] = useState<string | null>(null);

  if (loading) {
    return (
      <p className="text-center text-muted-foreground py-12">
        Cargando rutinas…
      </p>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-2xl p-6 border border-border text-center">
        <p className="text-orange-600 font-semibold mb-2">
          No pudimos cargar las rutinas
        </p>
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (!planes || planes.length === 0) {
    return (
      <div className="bg-card rounded-2xl p-8 border border-border text-center">
        <h3 className="text-lg font-semibold mb-2">
          Este paciente no tiene rutinas activas
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Prescribí una rutina nueva para que el paciente arranque con sus ejercicios.
        </p>
        <button
          onClick={onPrescribirRutina}
          className="bg-primary text-primary-foreground px-5 py-2 rounded-full text-sm font-semibold"
        >
          Prescribir rutina nueva
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      {planes.map((p) => (
        <PlanActivoCard
          key={p.id}
          plan={p}
          onDeleteOk={(ejercicioNombre) => {
            setToast(`"${ejercicioNombre}" eliminado de la rutina`);
            void refetch();
            window.setTimeout(() => setToast(null), 3000);
          }}
        />
      ))}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-green-600 text-white text-sm font-medium px-4 py-2 rounded-full shadow-lg animate-in fade-in slide-in-from-bottom-2 duration-200"
        >
          ✓ {toast}
        </div>
      )}
    </div>
  );
}

function PlanActivoCard({
  plan,
  onDeleteOk,
}: {
  plan: PlanActivo;
  onDeleteOk: (ejercicioNombre: string) => void;
}) {
  const fechaInicio = new Date(plan.fecha_inicio + 'T12:00:00').toLocaleDateString(
    'es-AR',
    { day: 'numeric', month: 'short', year: 'numeric' },
  );
  const frecuencia = plan.ejercicios[0]?.frecuencia_semanal ?? null;
  const isReadOnly = plan.tratamientoEstado !== 'activo';

  // Hook compartido entre todos los plan_ejercicios del card. Maneja debounce
  // + pending/errors per-field. Si se desmonta, cancela los timeouts.
  const updater = useActualizarPlanEjercicio();
  // Delete: hook compartido. Una instancia por card — al abrir el modal,
  // el target lo determina pendingDelete (qué plan_ejercicio borrar).
  const eliminator = useEliminarPlanEjercicio();
  const [pendingDelete, setPendingDelete] = useState<
    PlanActivo['ejercicios'][number] | null
  >(null);

  const closeModal = () => {
    setPendingDelete(null);
    eliminator.reset();
  };

  return (
    <div className="bg-card rounded-2xl p-6 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)]">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold">{plan.nombre}</h3>
          {isReadOnly && (
            <span
              className="text-[11px] px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 rounded-full"
              title="Este plan pertenece a un tratamiento no activo. Solo lectura."
            >
              Tratamiento cerrado — solo lectura
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground shrink-0">
          Inicio: {fechaInicio}
          {frecuencia !== null && ` · ${frecuencia}x/semana`}
        </p>
      </div>

      {plan.notas_kine && (
        <p className="text-sm text-muted-foreground italic mb-4">
          {plan.notas_kine}
        </p>
      )}

      <div className="space-y-2">
        {plan.ejercicios.map((pe) =>
          isReadOnly ? (
            <PlanEjercicioReadonlyRow key={pe.id} pe={pe} />
          ) : (
            <PlanEjercicioEditableRow
              key={pe.id}
              pe={pe}
              updater={updater}
              onRequestDelete={() => setPendingDelete(pe)}
            />
          ),
        )}
      </div>

      {pendingDelete && (
        <DeletePlanEjercicioModal
          pe={pendingDelete}
          onCancel={closeModal}
          onConfirm={async () => {
            const ok = await eliminator.eliminar(pendingDelete.id);
            if (ok) {
              const nombre = pendingDelete.ejercicio_nombre;
              closeModal();
              onDeleteOk(nombre);
            }
            // Si falló, el modal queda abierto mostrando el error (via
            // eliminator.error). El kine dismisses con "Entendido"/"Cerrar".
          }}
          loading={eliminator.loading}
          error={eliminator.error}
        />
      )}
    </div>
  );
}

/** Row readonly — render original pre-B4a. Usado cuando el tratamiento
 *  no está activo. Sprint 3 B4a PR3: para video_only muestra "Video
 *  tutorial" en lugar de "X×Y reps" para evitar confusión con
 *  defaults 1/1/0. */
function PlanEjercicioReadonlyRow({ pe }: { pe: PlanActivo['ejercicios'][number] }) {
  const isVideoOnly = isVideoOnlyExercise(pe.ejercicio_id);
  return (
    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
      <span className="text-muted-foreground font-medium tabular-nums text-sm">
        {pe.orden}.
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{pe.ejercicio_nombre}</p>
        {isVideoOnly ? (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <PlayCircle className="w-3 h-3" strokeWidth={1.75} />
            <span>Video tutorial</span>
            {pe.frecuencia_semanal !== null && (
              <span> · {pe.frecuencia_semanal}x/semana</span>
            )}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground tabular-nums">
            {pe.series_objetivo}×{pe.reps_objetivo} reps
            {pe.descanso_seg !== null && ` · ${pe.descanso_seg}s descanso`}
            {pe.frecuencia_semanal !== null &&
              ` · ${pe.frecuencia_semanal}x/semana`}
          </p>
        )}
      </div>
    </div>
  );
}

/** Row editable inline — Sprint 3 B4a. Inputs numéricos para series/reps/
 *  descanso/frecuencia + textarea para notas. Cambios debounced 500ms vía
 *  useActualizarPlanEjercicio. UI optimista: el state local del input se
 *  actualiza inmediato; si la RPC falla, el banner inline lo muestra
 *  (sin auto-revertir — el kine ve el valor que quiso poner + el error,
 *  decide cómo proceder).
 *
 *  Sub-decisión: NO auto-revertir post-error. Razón: si el kine puso
 *  "13" y la RPC falla por TRATAMIENTO_CERRADO, ver "13" + error es más
 *  claro que ver "10" original + error (que sugiere "no se guardó pero
 *  no sé qué quería poner"). El kine ajusta o refresca según prefiera. */
function PlanEjercicioEditableRow({
  pe,
  updater,
  onRequestDelete,
}: {
  pe: PlanActivo['ejercicios'][number];
  updater: ReturnType<typeof useActualizarPlanEjercicio>;
  onRequestDelete: () => void;
}) {
  const isVideoOnly = isVideoOnlyExercise(pe.ejercicio_id);

  // Local state del input — initial desde la prop (refleja DB).
  // Para video_only, reps/series/descanso no se editan (los inputs no
  // se renderean) pero mantenemos el state local para coherencia del
  // helper handleField si por algún motivo se acceden.
  const [reps, setReps] = useState<number>(pe.reps_objetivo);
  const [series, setSeries] = useState<number>(pe.series_objetivo);
  const [descanso, setDescanso] = useState<number | null>(pe.descanso_seg);
  const [frecuencia, setFrecuencia] = useState<number | null>(
    pe.frecuencia_semanal,
  );
  const [notas, setNotas] = useState<string>(pe.notas_kine ?? '');

  const handleField = (field: EditableField, value: number | string | null) => {
    updater.update(pe.id, field, value);
  };

  const isPending = (field: EditableField) =>
    updater.pending.has(`${pe.id}.${field}`);
  const errorOf = (field: EditableField) =>
    updater.errors.get(`${pe.id}.${field}`);

  // Helper para inputs numéricos: convierte "" a null para fields nullable.
  const numericOrNull = (v: string): number | null => {
    if (v.trim() === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  return (
    <div className="p-3 bg-muted/30 rounded-lg space-y-2">
      <div className="flex items-center gap-3">
        <span className="text-muted-foreground font-medium tabular-nums text-sm">
          {pe.orden}.
        </span>
        <p className="text-sm font-medium flex-1 min-w-0 truncate">
          {pe.ejercicio_nombre}
        </p>
        <button
          type="button"
          onClick={onRequestDelete}
          aria-label={`Eliminar ${pe.ejercicio_nombre} de la rutina`}
          title="Eliminar de la rutina"
          className="p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors"
        >
          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      {isVideoOnly ? (
        // Form simplificado para video_only: solo frecuencia editable.
        // reps/series/descanso quedan con defaults 1/1/0 — el paciente
        // sigue el video tutorial, esos campos no aplican. Badge sutil
        // para que el kine entienda por qué no ve los campos numéricos.
        <div className="space-y-2">
          <p className="text-[11px] text-muted-foreground italic flex items-center gap-1">
            <PlayCircle className="w-3 h-3" strokeWidth={1.75} />
            <span>Video tutorial — sin métricas de cámara</span>
          </p>
          <div className="grid grid-cols-2 gap-2 text-xs max-w-[280px]">
            <EditableNumberField
              label="Frec./sem"
              value={frecuencia}
              min={1}
              allowNull
              pending={isPending('frecuencia_semanal')}
              onChange={(v) => {
                setFrecuencia(v);
                handleField('frecuencia_semanal', v);
              }}
            />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <EditableNumberField
            label="Series"
            value={series}
            min={1}
            allowNull={false}
            pending={isPending('series_objetivo')}
            onChange={(v) => {
              if (v === null) return;
              setSeries(v);
              handleField('series_objetivo', v);
            }}
          />
          <EditableNumberField
            label="Reps"
            value={reps}
            min={1}
            allowNull={false}
            pending={isPending('reps_objetivo')}
            onChange={(v) => {
              if (v === null) return;
              setReps(v);
              handleField('reps_objetivo', v);
            }}
          />
          <EditableNumberField
            label="Descanso (s)"
            value={descanso}
            min={0}
            allowNull
            pending={isPending('descanso_seg')}
            onChange={(v) => {
              setDescanso(v);
              handleField('descanso_seg', v);
            }}
          />
          <EditableNumberField
            label="Frec./sem"
            value={frecuencia}
            min={1}
            allowNull
            pending={isPending('frecuencia_semanal')}
            onChange={(v) => {
              setFrecuencia(v);
              handleField('frecuencia_semanal', v);
            }}
          />
        </div>
      )}

      <div>
        <label className="text-xs text-muted-foreground">
          Notas del kine{' '}
          {isPending('notas_kine') && (
            <span className="text-[10px] text-primary/70">guardando…</span>
          )}
        </label>
        <textarea
          value={notas}
          onChange={(e) => {
            setNotas(e.target.value);
            handleField(
              'notas_kine',
              e.target.value.length === 0 ? null : e.target.value,
            );
          }}
          rows={2}
          placeholder="Ej: bajar peso si hay dolor"
          className="w-full mt-0.5 px-2 py-1.5 text-xs bg-background border border-border rounded resize-none focus:ring-1 focus:ring-ring focus:outline-none"
        />
      </div>

      {/* Errores per-field — render inline. */}
      {(['reps_objetivo', 'series_objetivo', 'descanso_seg', 'frecuencia_semanal', 'notas_kine'] as EditableField[]).map(
        (f) => {
          const err = errorOf(f);
          if (!err) return null;
          return (
            <div
              key={f}
              role="alert"
              className="flex items-start justify-between gap-2 px-2 py-1 bg-red-50 border border-red-200 rounded text-[11px] text-red-900"
            >
              <span>
                <span className="font-medium">{labelForField(f)}:</span>{' '}
                {err.message}
              </span>
              <button
                type="button"
                onClick={() => updater.dismissError(pe.id, f)}
                className="text-red-700 hover:text-red-900 text-[10px] font-semibold"
              >
                cerrar
              </button>
            </div>
          );
        },
      )}

      {/* Helper "Guardando…" inline para feedback de fields numéricos. */}
      {(isPending('reps_objetivo') ||
        isPending('series_objetivo') ||
        isPending('descanso_seg') ||
        isPending('frecuencia_semanal')) && (
        <p className="text-[10px] text-muted-foreground italic">Guardando…</p>
      )}
    </div>
  );
}

function EditableNumberField({
  label,
  value,
  min,
  allowNull,
  pending,
  onChange,
}: {
  label: string;
  value: number | null;
  min: number;
  allowNull: boolean;
  pending: boolean;
  onChange: (v: number | null) => void;
}) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-muted-foreground">{label}</span>
      <input
        type="number"
        inputMode="numeric"
        min={min}
        value={value ?? ''}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') {
            if (allowNull) onChange(null);
            return;
          }
          const n = Number(raw);
          if (Number.isFinite(n)) onChange(n);
        }}
        className={`px-2 py-1 bg-background border rounded tabular-nums focus:outline-none focus:ring-1 focus:ring-ring ${
          pending ? 'border-primary/40' : 'border-border'
        }`}
      />
    </label>
  );
}

function labelForField(f: EditableField): string {
  switch (f) {
    case 'reps_objetivo':
      return 'Reps';
    case 'series_objetivo':
      return 'Series';
    case 'descanso_seg':
      return 'Descanso';
    case 'frecuencia_semanal':
      return 'Frecuencia';
    case 'notas_kine':
      return 'Notas';
  }
}

// ─── Subcomponents ────────────────────────────────────────────────────

function InlineStat({
  value,
  label,
  hint,
}: {
  value: number | string;
  label: string;
  hint: string;
}) {
  return (
    <div className="text-center">
      <div className="text-3xl text-primary font-[800] tabular-nums">
        {value}
      </div>
      <p className="text-sm text-muted-foreground mt-1">{label}</p>
      <p className="text-xs text-muted-foreground/70">{hint}</p>
    </div>
  );
}

/** Stat inline "Adherencia" con wire a get_adherencia_paciente ventana
 *  'total' (Q8=A). Ver F-02 en SPEC. Estados:
 *  - loading: shimmer dot.
 *  - ok data: %.
 *  - has_frecuencia=false: — / hint "Falta frecuencia semanal".
 *  - VENTANA_MUY_CORTA: — / hint "Plan recién iniciado…".
 *  - NO_PLAN_ACTIVO: — / hint "Sin plan activo".
 *  - NOT_FOUND / UNKNOWN / error: — / hint "Error al calcular". */
function AdherenciaInlineStat({
  pacienteId,
}: {
  pacienteId: string | undefined;
}) {
  const { data, loading } = useAdherenciaPaciente(pacienteId, 'total');

  if (loading) {
    return (
      <div className="text-center">
        <div className="h-8 w-12 mx-auto bg-muted rounded animate-pulse" />
        <p className="text-sm text-muted-foreground mt-1">Adherencia</p>
        <p className="text-xs text-muted-foreground/70">Calculando…</p>
      </div>
    );
  }

  const { value, hint } = adherenciaInlineDisplay(data);
  return (
    <div className="text-center">
      <div className="text-3xl text-primary font-[800] tabular-nums">
        {value}
      </div>
      <p className="text-sm text-muted-foreground mt-1">Adherencia</p>
      <p className="text-xs text-muted-foreground/70">{hint}</p>
    </div>
  );
}

function adherenciaInlineDisplay(data: AdherenciaResult | null): {
  value: string;
  hint: string;
} {
  if (!data) return { value: '—', hint: 'Error al calcular' };
  if (data.code === 'NO_AUTH' || data.code === 'NOT_FOUND')
    return { value: '—', hint: 'Error al calcular' };
  if (data.code === 'NO_PLAN_ACTIVO')
    return { value: '—', hint: 'Sin plan activo' };
  if (data.code === 'UNKNOWN')
    return { value: '—', hint: 'Error al calcular' };
  if (data.has_frecuencia === false)
    return { value: '—', hint: 'Falta frecuencia semanal' };
  if (data.code === 'VENTANA_MUY_CORTA')
    return {
      value: '—',
      hint: 'Plan recién iniciado, todavía sin datos de adherencia',
    };
  if (typeof data.adherencia_pct === 'number')
    return {
      value: `${data.adherencia_pct}%`,
      hint: 'Desde inicio del plan',
    };
  return { value: '—', hint: 'Error al calcular' };
}

/** Card "Adherencia últimos 30 días" — ventana '30d' (Q8=A).
 *  Reemplaza ChartPlaceholder. Muestra número grande + counts
 *  "X de Y sesiones completadas". Sin score_promedio (scope cut).
 *  Sin chart temporal en fase 1 (chart de barras diaria = fase 2). */
function Adherencia30dCard({
  pacienteId,
}: {
  pacienteId: string | undefined;
}) {
  const { data, loading } = useAdherenciaPaciente(pacienteId, '30d');

  return (
    <div className="bg-card rounded-2xl p-6 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)] flex flex-col">
      <h3 className="mb-1 font-semibold">Adherencia últimos 30 días</h3>
      <p className="text-sm text-muted-foreground mb-6">
        Sesiones completadas vs esperadas
      </p>
      <div className="flex-1 flex items-center justify-center min-h-[200px] bg-muted/30 rounded-xl border border-dashed border-border p-6">
        {loading ? (
          <div className="text-center animate-pulse">
            <div className="h-12 w-24 bg-muted rounded mx-auto mb-2" />
            <div className="h-4 w-40 bg-muted rounded mx-auto" />
          </div>
        ) : (
          <Adherencia30dBody data={data} />
        )}
      </div>
    </div>
  );
}

function Adherencia30dBody({ data }: { data: AdherenciaResult | null }) {
  if (!data) {
    return (
      <p className="text-sm text-muted-foreground italic text-center">
        Error al calcular la adherencia.
      </p>
    );
  }
  if (data.code === 'NO_AUTH' || data.code === 'NOT_FOUND' || data.code === 'UNKNOWN') {
    return (
      <p className="text-sm text-muted-foreground italic text-center">
        Error al calcular la adherencia.
      </p>
    );
  }
  if (data.code === 'NO_PLAN_ACTIVO') {
    return (
      <p className="text-sm text-muted-foreground italic text-center">
        Sin plan activo.
      </p>
    );
  }
  if (data.has_frecuencia === false) {
    return (
      <p className="text-sm text-muted-foreground italic text-center">
        Falta configurar frecuencia semanal en el plan activo.
      </p>
    );
  }
  if (data.code === 'VENTANA_MUY_CORTA') {
    return (
      <p className="text-sm text-muted-foreground italic text-center">
        Plan recién iniciado, todavía sin datos de adherencia.
      </p>
    );
  }
  if (typeof data.adherencia_pct === 'number') {
    const completadas = data.sesiones_completadas ?? 0;
    const esperadas = data.sesiones_esperadas ?? 0;
    return (
      <div className="text-center">
        <div className="text-5xl font-[800] text-primary tabular-nums">
          {data.adherencia_pct}%
        </div>
        <p className="text-sm text-muted-foreground mt-3 tabular-nums">
          {completadas} de {esperadas}{' '}
          {esperadas === 1 ? 'sesión completada' : 'sesiones completadas'}
        </p>
      </div>
    );
  }
  return (
    <p className="text-sm text-muted-foreground italic text-center">
      Sin datos suficientes todavía.
    </p>
  );
}


function SesionRecienteRow({ sesion }: { sesion: SesionReciente }) {
  const fechaLabel = formatRelativeDate(new Date(sesion.ended_at));
  const qualityPct =
    sesion.score_promedio !== null
      ? Math.round(sesion.score_promedio * 100)
      : null;
  const duracionLabel =
    sesion.duration_sec !== null ? formatDuration(sesion.duration_sec) : '—';
  // Color del dot: verde si calidad ≥70, ámbar si 40-70, naranja si <40, gris si null.
  const dotClass =
    qualityPct === null
      ? 'bg-muted'
      : qualityPct >= 70
        ? 'bg-success'
        : qualityPct >= 40
          ? 'bg-[#F59E0B]'
          : 'bg-orange-500';

  return (
    <Link
      to={`/sesion-kine/${sesion.id}`}
      className="block py-4 border-b border-border last:border-0 transition-all duration-200 ease-out hover:bg-muted/30"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className={`w-3 h-3 rounded-full ${dotClass}`}></div>
          <div>
            <p className="font-medium">{fechaLabel}</p>
            <p className="text-sm text-muted-foreground">
              {sesion.total_reps} reps válidos
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 md:gap-8">
          <span className="text-muted-foreground tabular-nums">
            {qualityPct !== null ? `${qualityPct}%` : '—'}
          </span>
          <span className="text-muted-foreground w-16 tabular-nums">
            {duracionLabel}
          </span>
        </div>
      </div>
      {sesion.dolor_reportado !== null && (
        <DolorReportadoRow dolor={sesion.dolor_reportado} />
      )}
    </Link>
  );
}

/** Sub-línea que muestra el dolor reportado por el paciente en una
 *  sesión. Hidden si dolor_reportado es null (sesión pre-MVP o paciente
 *  no confirmó). Icono Smile/Meh/Frown según bucket:
 *    - Smile verde: 1-3 (nada + leve)
 *    - Meh ámbar: 4-5 (moderado)
 *    - Frown rojo: 6-10 (fuerte + intenso)
 *  Los buckets coalescen los 5 buckets de labelForLevel
 *  (nada/leve/moderado/fuerte/intenso) en 3 visuales — refuerzo binario
 *  positivo/neutro/negativo. El número exacto + label da granularidad fina. */
function DolorReportadoRow({ dolor }: { dolor: number }) {
  const { Icon, colorClass, label } = dolorVisualForLevel(dolor);
  return (
    <div className="flex items-center gap-2 mt-2 ml-7 text-sm">
      <Icon className={`w-4 h-4 ${colorClass}`} strokeWidth={1.5} />
      <span className="text-muted-foreground">
        Dolor:{' '}
        <span className={`font-medium tabular-nums ${colorClass}`}>
          {dolor}/10
        </span>{' '}
        <span className="capitalize">{label}</span>
      </span>
    </div>
  );
}

function dolorVisualForLevel(n: number): {
  Icon: typeof Smile;
  colorClass: string;
  label: string;
} {
  if (n <= 3) {
    return {
      Icon: Smile,
      colorClass: 'text-green-600 dark:text-green-400',
      label: n === 1 ? 'nada' : 'leve',
    };
  }
  if (n <= 5) {
    return {
      Icon: Meh,
      colorClass: 'text-amber-600 dark:text-amber-400',
      label: 'moderado',
    };
  }
  return {
    Icon: Frown,
    colorClass: 'text-red-600 dark:text-red-400',
    label: n <= 7 ? 'fuerte' : 'intenso',
  };
}

// ─── Loading / error states ───────────────────────────────────────────

function DetalleSkeleton() {
  return (
    <>
      <div className="h-5 w-48 bg-muted rounded animate-pulse mb-6" />
      <div className="bg-card rounded-2xl p-4 md:p-8 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)] mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 animate-pulse">
          <div className="w-20 h-20 rounded-full bg-muted" />
          <div className="space-y-2 flex-1 min-w-0">
            <div className="h-8 w-full max-w-64 bg-muted rounded" />
            <div className="h-4 w-full max-w-80 bg-muted rounded" />
          </div>
        </div>
      </div>
    </>
  );
}

function NotFoundState({ onVolver }: { onVolver: () => void }) {
  return (
    <div className="bg-card rounded-2xl p-12 border border-border text-center">
      <h1 className="text-2xl font-semibold mb-2">Paciente no encontrado</h1>
      <p className="text-sm text-muted-foreground mb-6">
        El link no apunta a un paciente existente.
      </p>
      <button
        onClick={onVolver}
        className="bg-primary text-primary-foreground px-5 py-3 rounded-full text-sm font-semibold transition-all hover:scale-[1.02]"
      >
        Volver al listado
      </button>
    </div>
  );
}

function FetchErrorState({
  message,
  onVolver,
}: {
  message: string;
  onVolver: () => void;
}) {
  return (
    <div className="bg-card rounded-2xl p-12 border border-border text-center">
      <h1 className="text-2xl font-semibold text-orange-600 mb-2">
        No pudimos cargar los datos del paciente
      </h1>
      <p className="text-sm text-muted-foreground mb-6">{message}</p>
      <button
        onClick={onVolver}
        className="bg-primary text-primary-foreground px-5 py-3 rounded-full text-sm font-semibold transition-all hover:scale-[1.02]"
      >
        Volver al listado
      </button>
    </div>
  );
}

// ─── Delete plan_ejercicio modal ──────────────────────────────────────
// Modal con 3 estados visuales que cambian según `error`:
//   1. Sin error → estado "confirm": pregunta + Cancelar/Eliminar.
//   2. error.code === 'HAS_SESSIONS' → estado "blocked-sessions": banner
//      amarillo con count + "Entendido".
//   3. error.code === 'TRATAMIENTO_CERRADO' → estado "blocked-tratamiento":
//      banner gris + "Entendido". (Defense in depth — el botón Trash no
//      debería verse en readonly, pero si llegamos acá lo manejamos.)
//   4. error.code en {NOT_FOUND, NO_AUTH, UNKNOWN} → estado "error":
//      banner rojo con mensaje según code + "Cerrar".
//
// Patrón inline modal (sin AlertDialog shadcn) consistente con
// DeleteConfirmDialog que ya existe en este mismo archivo (Sprint 2 B7).

function DeletePlanEjercicioModal({
  pe,
  onCancel,
  onConfirm,
  loading,
  error,
}: {
  pe: PlanActivo['ejercicios'][number];
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
  error: DeleteError | null;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="bg-card rounded-2xl border border-border shadow-xl max-w-md w-full p-6">
        {/* Estado: error HAS_SESSIONS — blocked amarillo */}
        {error?.code === 'HAS_SESSIONS' && (
          <DeleteBlockedBySessionsView
            pe={pe}
            count={error.count ?? 0}
            onClose={onCancel}
          />
        )}
        {/* Estado: error TRATAMIENTO_CERRADO — blocked gris (defense in depth) */}
        {error?.code === 'TRATAMIENTO_CERRADO' && (
          <DeleteBlockedByTratamientoView onClose={onCancel} />
        )}
        {/* Estado: error genérico — rojo */}
        {error &&
          error.code !== 'HAS_SESSIONS' &&
          error.code !== 'TRATAMIENTO_CERRADO' && (
            <DeleteGenericErrorView code={error.code} onClose={onCancel} />
          )}
        {/* Estado por defecto: confirmación */}
        {!error && (
          <DeleteConfirmView
            pe={pe}
            onCancel={onCancel}
            onConfirm={onConfirm}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
}

function DeleteConfirmView({
  pe,
  onCancel,
  onConfirm,
  loading,
}: {
  pe: PlanActivo['ejercicios'][number];
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
}) {
  return (
    <>
      <h2 className="text-lg font-semibold mb-2">
        Eliminar ejercicio de la rutina
      </h2>
      <p className="text-sm text-muted-foreground mb-6">
        ¿Eliminar{' '}
        <span className="font-medium text-foreground">
          {pe.ejercicio_nombre}
        </span>{' '}
        de la rutina? Esta acción no se puede deshacer.
      </p>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-muted/50 transition-colors disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={loading}
          className="bg-red-600 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
        >
          {loading ? 'Eliminando…' : 'Eliminar'}
        </button>
      </div>
    </>
  );
}

function DeleteBlockedBySessionsView({
  pe,
  count,
  onClose,
}: {
  pe: PlanActivo['ejercicios'][number];
  count: number;
  onClose: () => void;
}) {
  return (
    <>
      <h2 className="text-lg font-semibold mb-2">
        No se puede eliminar
      </h2>
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6">
        <p className="text-sm text-amber-900 leading-relaxed">
          <span className="font-medium">{pe.ejercicio_nombre}</span> tiene{' '}
          <span className="font-medium tabular-nums">{count}</span>{' '}
          {count === 1 ? 'sesión registrada' : 'sesiones registradas'}.
          No se puede eliminar para preservar el histórico clínico.
        </p>
        <p className="text-xs text-amber-800/80 mt-2">
          Si querés sacarlo de la rutina activa, considerá cerrar el
          tratamiento.
        </p>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="bg-primary text-primary-foreground px-4 py-2 rounded-full text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          Entendido
        </button>
      </div>
    </>
  );
}

function DeleteBlockedByTratamientoView({
  onClose,
}: {
  onClose: () => void;
}) {
  return (
    <>
      <h2 className="text-lg font-semibold mb-2">
        Tratamiento cerrado
      </h2>
      <div className="bg-muted/50 border border-border rounded-xl px-4 py-3 mb-6">
        <p className="text-sm text-foreground leading-relaxed">
          El tratamiento está cerrado. No se pueden eliminar ejercicios de
          tratamientos cerrados.
        </p>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="bg-primary text-primary-foreground px-4 py-2 rounded-full text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          Entendido
        </button>
      </div>
    </>
  );
}

function DeleteGenericErrorView({
  code,
  onClose,
}: {
  code: DeleteError['code'];
  onClose: () => void;
}) {
  const message =
    code === 'NOT_FOUND'
      ? 'No se encontró el ejercicio. Puede haber sido eliminado por otra sesión.'
      : code === 'NO_AUTH'
        ? 'Sesión expirada. Iniciá sesión nuevamente.'
        : 'Ocurrió un error al eliminar. Volvé a intentar.';
  return (
    <>
      <h2 className="text-lg font-semibold mb-2">No pudimos eliminar</h2>
      <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-6">
        <p className="text-sm text-red-900 leading-relaxed">{message}</p>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-muted/50 transition-colors"
        >
          Cerrar
        </button>
      </div>
    </>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────

function formatEdad(fechaNacimiento: string | null): string | null {
  if (!fechaNacimiento) return 'Edad no registrada';
  const hoy = new Date();
  const fn = new Date(fechaNacimiento);
  let edad = hoy.getFullYear() - fn.getFullYear();
  const mesActual = hoy.getMonth();
  const mesNacimiento = fn.getMonth();
  if (
    mesActual < mesNacimiento ||
    (mesActual === mesNacimiento && hoy.getDate() < fn.getDate())
  ) {
    edad--;
  }
  return `${edad} años`;
}

/** "Semana N de M" si fecha_fin existe; "Semana N" si no. La cuenta
 *  es desde fecha_inicio (semana 1 = primera semana del tratamiento). */
function formatSemana(fechaInicio: string, fechaFin: string | null): string {
  const inicioMs = new Date(fechaInicio).getTime();
  const semanasDesdeInicio =
    Math.floor((Date.now() - inicioMs) / (7 * 86_400_000)) + 1;
  if (!fechaFin) return `Semana ${semanasDesdeInicio}`;
  const finMs = new Date(fechaFin).getTime();
  const semanasTotales = Math.ceil((finMs - inicioMs) / (7 * 86_400_000));
  return `Semana ${semanasDesdeInicio} de ${semanasTotales}`;
}

function formatDuration(sec: number): string {
  const mm = Math.floor(sec / 60);
  const ss = Math.floor(sec % 60);
  return `${mm}:${ss.toString().padStart(2, '0')}`;
}

// ─── Invitation actions ──────────────────────────────────────────────
// Renderea "Copiar link" o "Regenerar invitación" según si el token
// vigente o expiró. Mejor UX: no obligar al kine a regenerar si el
// link todavía sirve.

function InvitationActions({
  expiresAt,
  onCopiar,
  onRegenerar,
  regenerando,
}: {
  expiresAt: string | null;
  onCopiar: () => void;
  onRegenerar: () => void;
  regenerando: boolean;
}) {
  const isExpired =
    !expiresAt || new Date(expiresAt).getTime() < Date.now();

  if (isExpired) {
    return (
      <button
        onClick={onRegenerar}
        disabled={regenerando}
        className="bg-card border border-border px-4 py-2.5 rounded-full flex items-center gap-2 text-sm font-medium transition-all duration-200 ease-out hover:bg-muted/50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <RefreshCw className={`w-4 h-4 ${regenerando ? 'animate-spin' : ''}`} strokeWidth={1.5} />
        {regenerando ? 'Regenerando…' : 'Regenerar invitación'}
      </button>
    );
  }

  return (
    <button
      onClick={onCopiar}
      className="bg-card border border-border px-4 py-2.5 rounded-full flex items-center gap-2 text-sm font-medium transition-all duration-200 ease-out hover:bg-muted/50"
    >
      <LinkIcon className="w-4 h-4" strokeWidth={1.5} />
      Copiar invitación
    </button>
  );
}

// ─── Delete confirm dialog ───────────────────────────────────────────
// Modal simple de confirmación. Muestra error de la RPC inline si el
// delete fue bloqueado (sesiones / tratamientos / cuenta activada).

function DeleteConfirmDialog({
  open,
  onOpenChange,
  pacienteNombre,
  onConfirm,
  submitting,
  errorMessage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pacienteNombre: string;
  onConfirm: () => void;
  submitting: boolean;
  errorMessage: string | null;
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="bg-card rounded-2xl border border-border shadow-xl max-w-md w-full p-6">
        <h2 className="text-lg font-semibold mb-2">Eliminar paciente</h2>
        <p className="text-sm text-muted-foreground mb-1">
          ¿Estás seguro que querés eliminar a{' '}
          <span className="font-medium text-foreground">{pacienteNombre}</span>?
        </p>
        <p className="text-xs text-muted-foreground mb-4">
          Solo se puede eliminar si el paciente todavía no activó su cuenta y no tiene
          sesiones ni tratamientos registrados. Esta acción no se puede deshacer.
        </p>

        {errorMessage && (
          <div
            role="alert"
            className="text-sm rounded-md px-3 py-2 bg-red-50 text-red-900 border border-red-200 mb-4"
          >
            {errorMessage}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-muted/50 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="bg-red-600 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            {submitting ? 'Eliminando…' : 'Eliminar'}
          </button>
        </div>
      </div>
    </div>
  );
}
